import {
  createRoute,
  createRouter,
  createRootRouteWithContext,
  redirect,
  type ParsedLocation,
  Outlet,
  notFound,
  HeadContent
} from '@tanstack/react-router';
import { InternalServerError } from '@r/page/error/intenal-server-error.tsx';
import { NotFoundError } from '@r/page/error/not-found-error.tsx';
import { Loader } from '@r/components/loader/loader.tsx';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { getSafeRedirectPath } from '@r/utils/redirect.ts';
import HomePage from '@client/pages/home/home.tsx';
import { ProgramPage } from '@client/pages/program/program.tsx';
import { PersonalInformations } from '@client/pages/account/personal-informations.tsx';
import { CompanyInformations } from '@client/pages/account/company-informations.tsx';
import { Account } from '@client/pages/account/account.tsx';
import type {
  GroupedWorkspaceConfigModel,
  RouteRouteReadModel,
  UserMeReadModel
} from '@r/api/generated/client.schemas.ts';
import Auth from '@client/pages/auth/auth.tsx';
import ResetPassword from '@client/pages/reset-password/reset-password.tsx';
import ResetPasswordCallback from '@client/pages/reset-password/reset-password-callback.tsx';
import { FirstLoginPage } from '@r/components/auth/first-login-page.tsx';
import AccountLayout from '@client/pages/layout/account-layout.tsx';
import MainLayout from '@client/pages/layout/main-layout.tsx';
import { lazy } from 'react';
import { PublicLayoutComponent } from '@r/components/router/public-layout-component.tsx';
import { TermsAndConditions } from '@client/pages/auth/terms-and-conditions.tsx';
import PerformancePage from '@client/pages/performances/performances.tsx';
import { Contact } from '@client/pages/account/contact.tsx';
import { Changelogs } from '@client/pages/account/changelogs.tsx';
import PdfViewer from '@r/components/pdf/pdf-viewer.tsx';
import { ChangePassword } from '@client/pages/auth/change-password.tsx';
import { Checkout } from '@client/pages/cart/cart.tsx';
import { Shops } from '@client/pages/shops/shops.tsx';
import { ProductPage } from '@client/pages/shops/product.tsx';
import { ProductCatalogPage } from '@client/pages/shops/product-catalog.tsx';
import { Orders } from '@client/pages/account/orders.tsx';
import { Order } from '@r/components/order/order-detail.tsx';
import { Games } from '@client/pages/games/games.tsx';
import { GameDetail } from '@client/pages/games/game-detail.tsx';
import AutoLoginCallback from '@client/pages/autologin/autologin-callback.tsx';
import { Invoices } from '@client/pages/invoices/invoices.tsx';
import { PurchaseProofHistory } from '@client/pages/account/purchase-proofs.tsx';
import { isPurchaseProofFeatureEnabled } from '@client/components/invoices/purchase-proof-feature.ts';

const TanStackRouterDevtools =
  process.env.NODE_ENV === 'production'
    ? () => null // Render nothing in production
    : lazy(() =>
        // Lazy load in development
        import('@tanstack/react-router-devtools').then((res) => ({
          default: res.TanStackRouterDevtools
          // For Embedded Mode
          // default: res.TanStackRouterDevtoolsPanel
        }))
      );

interface MyRouterContext {
  user: UserMeReadModel | null;
  routes: RouteRouteReadModel[];
  routesLoaded?: boolean;
  workspaceConfig?: GroupedWorkspaceConfigModel;
  workspacePageTitleSuffix: string;
}

const checkAndRedirect = (context: MyRouterContext, _location: ParsedLocation, path: string) => {
  // Attendre que les routes soient chargées et non vides avant de vérifier
  if (!context.routesLoaded || !context.routes?.length) {
    return;
  }
  const matchingRoute = context.routes.find((route) => route.path == path);

  if (!matchingRoute) {
    throw notFound();
  }
};

/** Restricts purchase-proof pages to workspaces where the feature is explicitly enabled. */
const validatePurchaseProofFeature = (context: MyRouterContext) => {
  if (!isPurchaseProofFeatureEnabled(context.workspaceConfig)) {
    throw notFound();
  }
};

const getClientPageTitle = (pageTitle: string | undefined, workspaceSuffix: string) =>
  pageTitle ? `${pageTitle} | ${workspaceSuffix}` : workspaceSuffix;

const getHead =
  (pageTitle?: string) =>
  ({ match }: { match: { context: MyRouterContext } }) => ({
    meta: [{ title: getClientPageTitle(pageTitle, match.context.workspacePageTitleSuffix) }]
  });

const rootRoute = createRootRouteWithContext<MyRouterContext>()({
  head: getHead(),
  component: () => {
    return (
      <>
        <HeadContent />
        <Outlet />
        <TanStackRouterDevtools position={'bottom-right'} />
      </>
    );
  },
  errorComponent: ({ error }) => {
    return <InternalServerError error={error} />;
  },
  notFoundComponent: () => {
    return <NotFoundError />;
  }
});

const validateUserAuth = (context: MyRouterContext, location: ParsedLocation) => {
  if (!context.user || localStorage.getItem('token') === null) {
    throw redirect({
      to: '/login',
      search: {
        redirect: location.href
      }
    });
  }
};

const validateUserStatus = (context: MyRouterContext) => {
  if (localStorage.getItem('token') && context?.user) {
    const activeWorkspace = getActiveWorkspaceUniversal(context.user.visibleWorkspaces);

    if (!activeWorkspace) {
      throw redirect({
        to: '/login',
        search: {
          error: 'workspace-unavailable'
        }
      });
    }

    if (activeWorkspace.status === 'user.status.unregistered') {
      throw redirect({
        to: '/terms-of-use'
      });
    }

    if (activeWorkspace.passwordNeedToBeChanged) {
      throw redirect({
        to: '/change-password'
      });
    }
  }
};

const AuthLayout = createRoute({
  getParentRoute: () => rootRoute,
  beforeLoad: ({ context, location }) => {
    validateUserAuth(context, location);
    validateUserStatus(context);
  },
  id: 'auth-layout',
  component: function Index() {
    return <Outlet />;
  }
});

const AuthMainLayout = createRoute({
  getParentRoute: () => AuthLayout,
  id: 'auth-main-layout',
  component: function Index() {
    return <MainLayout />;
  }
});

const AuthHome = createRoute({
  getParentRoute: () => AuthMainLayout,
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/');
  },
  path: '/',
  head: getHead('Accueil'),
  component: HomePage
});

const AuthShops = createRoute({
  getParentRoute: () => AuthMainLayout,
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/shops');
  },
  path: '/shops',
  head: getHead('Boutiques'),
  component: Shops
});

const AuthGames = createRoute({
  getParentRoute: () => AuthMainLayout,
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/games');
  },
  path: '/games',
  head: getHead('Animations'),
  component: Games
});

const AuthGameDetail = createRoute({
  getParentRoute: () => AuthMainLayout,
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/games/$id');
  },
  path: '/games/$id',
  head: getHead("Détail de l'animation"),
  component: GameDetail
});

const AuthShopCatalog = createRoute({
  getParentRoute: () => AuthMainLayout,
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/shops/$shopId/products');
  },
  path: '/shops/$shopId/products',
  head: getHead('Catalogue produits'),
  component: ProductCatalogPage
});

const AuthShopCatalogProduct = createRoute({
  getParentRoute: () => AuthMainLayout,
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/shops/$shopId/products/$id');
  },
  path: '/shops/$shopId/products/$id',
  head: getHead('Détail produit'),
  component: ProductPage
});

const AuthOrders = createRoute({
  getParentRoute: () => AuthAccountLayout,
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/orders');
  },
  path: '/orders',
  head: getHead('Mes récompenses'),
  component: Orders
});

const AuthOrder = createRoute({
  getParentRoute: () => AuthAccountLayout,
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/orders/$id');
  },
  path: '/orders/$id',
  head: getHead('Détail récompense'),
  component: () => <Order />
});

const AuthPurchaseProofHistory = createRoute({
  getParentRoute: () => AuthAccountLayout,
  beforeLoad: ({ context }) => {
    validatePurchaseProofFeature(context);
  },
  path: '/user/invoices',
  head: getHead('Suivi des dépôts de justificatifs'),
  component: PurchaseProofHistory
});

const AuthProgram = createRoute({
  getParentRoute: () => AuthMainLayout,
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/program');
  },
  path: '/program',
  head: getHead('Mon programme'),
  component: ProgramPage
});

const AuthPerformance = createRoute({
  getParentRoute: () => AuthMainLayout,
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/performance');
  },
  path: '/performance',
  head: getHead('Mes performances'),
  component: PerformancePage
});

const AuthInvoices = createRoute({
  getParentRoute: () => AuthMainLayout,
  beforeLoad: ({ context }) => {
    validatePurchaseProofFeature(context);
  },
  path: '/invoices',
  head: getHead('Déposer un justificatif'),
  component: Invoices
});

const AuthAccountLayout = createRoute({
  getParentRoute: () => AuthMainLayout,
  id: 'auth-account-layout',
  component: AccountLayout
});

const AuthAccount = createRoute({
  getParentRoute: () => AuthAccountLayout,
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/account');
  },
  path: '/account',
  head: getHead('Mon compte'),
  component: function Index() {
    return <Account />;
  }
});

const AuthPersonalInformations = createRoute({
  getParentRoute: () => AuthAccountLayout,
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/personal-informations');
  },
  path: '/personal-informations',
  head: getHead('Informations personnelles'),
  component: function Index() {
    return <PersonalInformations />;
  }
});

const AuthCompanyInformations = createRoute({
  getParentRoute: () => AuthAccountLayout,
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/company-informations');
  },
  path: '/company-informations',
  head: getHead('Informations entreprise'),
  component: function Index() {
    return <CompanyInformations />;
  }
});

const AuthCart = createRoute({
  getParentRoute: () => AuthMainLayout,
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/cart');
  },
  path: '/cart',
  head: getHead('Mon panier'),
  component: function Index() {
    return <Checkout />;
  }
});

const AuthContact = createRoute({
  getParentRoute: () => AuthAccountLayout,
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/contact');
  },
  path: '/contact',
  head: getHead('Nous contacter'),
  component: function Index() {
    return <Contact />;
  }
});

const AuthChangelogs = createRoute({
  getParentRoute: () => AuthAccountLayout,
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/changelogs');
  },
  path: '/changelogs',
  head: getHead('Nouveautés programme'),
  component: function Index() {
    return <Changelogs />;
  }
});

const AuthTermsAndConditions = createRoute({
  getParentRoute: () => AuthMainLayout,
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/terms-and-conditions');
  },
  path: '/terms-and-conditions',
  head: getHead("Règlement de l'opération"),
  component: function Index() {
    return <PdfViewer mediaKey={'regulation'} />;
  }
});

const AuthPrivacyPolicy = createRoute({
  getParentRoute: () => AuthMainLayout,
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/privacy-policy');
  },
  path: '/privacy-policy',
  head: getHead('Politique de confidentialité'),
  component: function Index() {
    return <PdfViewer mediaKey={'privacy-policy'} />;
  }
});

const AuthLegalNotice = createRoute({
  getParentRoute: () => AuthMainLayout,
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/legal-notice');
  },
  path: '/legal-notice',
  head: getHead('Mentions légales'),
  component: function Index() {
    return <PdfViewer mediaKey={'legal-notice'} />;
  }
});

const PublicFirstLoginChangePassword = createRoute({
  getParentRoute: () => PublicLayoutFirstLoginChangePassword,
  beforeLoad: ({ context }) => {
    if (localStorage.getItem('token') && context?.user) {
      // If the user has already complete the passwordNeedToBeChanged
      // he already change this password so we redirect to "/"
      if (!context.user?.visibleWorkspaces[0].passwordNeedToBeChanged) {
        throw redirect({
          to: '/'
        });
      }
    } else {
      throw redirect({
        to: '/login'
      });
    }
  },
  path: '/change-password',
  head: getHead('Changement de mot de passe'),
  component: function Index() {
    return <ChangePassword />;
  }
});

const PublicTermsOfUse = createRoute({
  getParentRoute: () => PublicLayoutTermOfUse,
  beforeLoad: ({ context }) => {
    if (localStorage.getItem('token') && context?.user) {
      const visibleWorkspace = context.user.visibleWorkspaces[0];

      if (visibleWorkspace.status === 'user.status.registered') {
        if (visibleWorkspace.passwordNeedToBeChanged) {
          throw redirect({
            to: '/change-password'
          });
        }

        throw redirect({
          to: '/'
        });
      }
    } else {
      throw redirect({
        to: '/login'
      });
    }
  },
  path: '/terms-of-use',
  head: getHead("Conditions d'utilisation"),
  component: function Index() {
    return <TermsAndConditions />;
  }
});

const PublicLayout = createRoute({
  getParentRoute: () => rootRoute,
  beforeLoad: ({ context, location }) => {
    if (localStorage.getItem('token') && context?.user) {
      const firstWorkspace = context.user.visibleWorkspaces?.[0];

      if (!firstWorkspace) {
        return;
      }

      if (firstWorkspace.status === 'user.status.unregistered') {
        throw redirect({
          to: '/terms-of-use'
        });
      }

      if (firstWorkspace.passwordNeedToBeChanged) {
        throw redirect({
          to: '/change-password'
        });
      }

      const redirectPath = getSafeRedirectPath(location.search);
      throw redirectPath ? redirect({ href: redirectPath }) : redirect({ to: '/' });
    }
  },
  id: 'public-layout',
  component: PublicLayoutComponent
});

const PublicLayoutTermOfUse = createRoute({
  getParentRoute: () => rootRoute,
  id: 'public-layout-term-of-use',
  component: PublicLayoutComponent
});

const PublicLayoutFirstLoginChangePassword = createRoute({
  getParentRoute: () => rootRoute,
  id: 'public-layout-first-login-change-password',
  component: PublicLayoutComponent
});

const PublicLogin = createRoute({
  getParentRoute: () => PublicLayout,
  path: '/login',
  head: getHead('Connexion'),
  component: Auth
});

const PublicResetPassword = createRoute({
  getParentRoute: () => PublicLayout,
  path: 'reset-password'
});

const PublicResetPasswordIndex = createRoute({
  getParentRoute: () => PublicResetPassword,
  path: '/',
  head: getHead('Réinitialiser mon mot de passe'),
  component: ResetPassword
});

const PublicResetPasswordCallback = createRoute({
  getParentRoute: () => PublicResetPassword,
  path: '/$token',
  head: getHead('Nouveau mot de passe'),
  component: ResetPasswordCallback
});

const PublicFirstLogin = createRoute({
  getParentRoute: () => PublicLayout,
  path: '/first-login',
  head: getHead('Première connexion'),
  component: FirstLoginPage
});

const PublicAutoLoginLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: 'public-autologin-layout',
  component: PublicLayoutComponent
});

const PublicAutoLoginCallback = createRoute({
  getParentRoute: () => PublicAutoLoginLayout,
  path: '/autologin/$token',
  head: getHead('Connexion automatique'),
  component: AutoLoginCallback
});

const routeTree = rootRoute.addChildren([
  PublicLogin,
  PublicFirstLogin,
  PublicResetPassword,
  PublicResetPasswordCallback,
  PublicResetPasswordIndex,
  PublicAutoLoginLayout,
  PublicAutoLoginCallback,
  AuthCart,
  AuthHome,
  AuthShops,
  AuthGames,
  AuthGameDetail,
  AuthShopCatalog,
  AuthShopCatalogProduct,
  AuthOrders,
  AuthOrder,
  AuthPurchaseProofHistory,
  AuthProgram,
  AuthPerformance,
  AuthInvoices,
  AuthAccount,
  AuthLayout,
  AuthMainLayout,
  AuthAccountLayout,
  AuthTermsAndConditions,
  AuthPrivacyPolicy,
  AuthLegalNotice,
  PublicLayout,
  PublicTermsOfUse,
  PublicFirstLoginChangePassword,
  AuthPersonalInformations,
  AuthCompanyInformations,
  AuthContact,
  AuthChangelogs,
  PublicLayoutTermOfUse,
  PublicLayoutFirstLoginChangePassword
]);

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof clientRouter;
  }
}

export const clientRouter = createRouter({
  defaultNotFoundComponent: () => {
    return <NotFoundError />;
  },
  routeTree,
  defaultPreload: 'intent',
  notFoundMode: 'root',
  scrollRestoration: true,
  defaultPendingComponent: () => <Loader />,
  context: {
    user: {} as UserMeReadModel,
    routes: [] as RouteRouteReadModel[],
    routesLoaded: false,
    workspaceConfig: undefined,
    workspacePageTitleSuffix: 'Fiddy'
  },
  defaultErrorComponent: ({ error }) => {
    return <InternalServerError error={error} />;
  }
});
